import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Play, Info, Loader2, BarChart3, Scale, ExternalLink,
  ChevronDown, ChevronUp,
} from "lucide-react";

// Fairness Simulation tab (read-only, advisory only).
// Tweak 4: explanations are larger and more prominent than band badges.
// No red colour anywhere, greens, ambers and neutral slates only.

const BAND_META = {
  balanced:           { label: "Balanced",         bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", bar: "bg-emerald-400" },
  slightly_above:     { label: "Slightly above",   bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   bar: "bg-amber-300" },
  slightly_below:     { label: "Slightly below",   bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   bar: "bg-amber-300" },
  materially_above:   { label: "Materially above", bg: "bg-amber-100",  border: "border-amber-300",   text: "text-amber-800",   bar: "bg-amber-500" },
  materially_below:   { label: "Materially below", bg: "bg-amber-100",  border: "border-amber-300",   text: "text-amber-800",   bar: "bg-amber-500" },
  skipped:            { label: "Skipped",          bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-500",   bar: "bg-slate-200" },
};

function isoDaysFromToday(n) {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function FairnessSimulationTab() {
  const [fromDate, setFromDate] = useState(isoDaysFromToday(-60));
  const [toDate, setToDate] = useState(isoDaysFromToday(0));
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/simulations/fairness/latest?from_date=${fromDate}&to_date=${toDate}`);
        if (!cancelled) setSim(data);
      } catch (err) {
        if (err?.response?.status !== 404 && !cancelled)
          toast.error(formatError(err.response?.data?.detail) || "Failed to load fairness check");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (toDate < fromDate) { toast.error("End date must be on or after start date."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/simulations/fairness/run", {
        from_date: fromDate, to_date: toDate,
      });
      setSim(data);
      if (data.status === "completed") toast.success("Fairness check complete");
      else toast.error(`Failed: ${data.error_message || "see history"}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to run fairness check");
    } finally { setBusy(false); }
  };

  const summary = sim?.result_summary || null;
  const raw = sim?.raw_result || null;
  const scorecards = raw?.scorecards || [];
  const limitations = raw?.limitations || [];
  const teamAvg = summary?.team_averages || {};

  return (
    <div className="space-y-5" data-testid="fairness-sim-tab">
      <div className="rounded-lg border border-[#BFD3FB] bg-[#F0F6FF] p-3 flex gap-3"
           data-testid="fairness-help">
        <Info className="h-4 w-4 text-[#1D4ED8] mt-0.5 flex-shrink-0" />
        <div className="text-xs text-[#1D4ED8] leading-relaxed">
          <span className="font-medium">What does this check?</span>{" "}
          It looks backwards over the chosen window and surfaces where work is
          unevenly distributed across the team, total time worked, weekend
          share, late shifts, bank-holiday shifts, less-popular work and
          approved leave taken during this period.
          <br />
          <span className="font-medium">Always advisory.</span> Fairness signals
          inform conversations and rebalancing decisions. They never block
          coverage, skills or leave rules, and never make any decisions
          automatically.
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3"
           data-testid="fairness-controls">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-[#64748B]" htmlFor="f-from">From</Label>
          <Input id="f-from" type="date" value={fromDate}
                 onChange={(e) => setFromDate(e.target.value)}
                 className="mt-1 w-44" data-testid="fairness-from" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-[#64748B]" htmlFor="f-to">To</Label>
          <Input id="f-to" type="date" value={toDate}
                 onChange={(e) => setToDate(e.target.value)}
                 className="mt-1 w-44" data-testid="fairness-to" />
        </div>
        <Button onClick={run} disabled={busy} data-testid="fairness-run"
                className="bg-[#2563EB] hover:bg-[#1D4ED8]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {busy ? "Running…" : "Run fairness check"}
        </Button>
        {sim?.created_at && (
          <div className="text-[11px] text-[#64748B] ml-auto">
            Last run: {new Date(sim.created_at).toLocaleString("en-GB", {
              day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
            {sim.engine_version && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-widest text-[#1D4ED8] px-1.5 py-0.5 rounded bg-[#DBE7FE] border border-[#BFD3FB]">
                engine {sim.engine_version}
              </span>
            )}
          </div>
        )}
      </div>

      {!summary && !busy && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center"
             data-testid="fairness-empty">
          <Scale className="h-7 w-7 text-[#2563EB] mx-auto mb-2" />
          <p className="text-sm font-medium text-[#0B1A36]">No fairness check yet for this window</p>
          <p className="text-xs text-[#4B5773] mt-1">Pick a date range and click <strong>Run fairness check</strong>.</p>
        </div>
      )}

      {summary?.setup_state && summary.setup_state !== "ready" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-3"
             data-testid={`fairness-setup-${summary.setup_state}`}>
          <Info className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">
            <span className="font-medium">Heads-up:</span> {summary.setup_state_message}
          </div>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="fairness-kpis">
          <KpiTile label="Balanced employees" value={summary.balanced_employees || 0}
                   tone="emerald" hint="Within tolerance across every dimension." />
          <KpiTile label="Materially above" value={summary.outliers_above || 0}
                   tone="amber" hint="Above the team baseline on one or more dimensions." />
          <KpiTile label="Materially below" value={summary.outliers_below || 0}
                   tone="amber" hint="Below the team baseline on one or more dimensions." />
        </div>
      )}

      {summary && Object.keys(teamAvg).length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3"
             data-testid="fairness-team-avg">
          <div className="text-[11px] uppercase tracking-widest text-[#64748B] mb-2">Team averages</div>
          <div className="flex flex-wrap gap-4 text-xs text-[#0B1A36]">
            <span><strong>{teamAvg.total_hours?.toFixed(0)}h</strong> total time</span>
            <span><strong>{teamAvg.weekend_share?.toFixed(0)}%</strong> weekend</span>
            <span><strong>{teamAvg.late_share?.toFixed(0)}%</strong> late</span>
            <span><strong>{teamAvg.holiday_share?.toFixed(0)}%</strong> bank holiday</span>
            <span><strong>{teamAvg.undesired_share?.toFixed(0)}%</strong> less popular</span>
            <span><strong>{teamAvg.leave_share?.toFixed(1)}</strong> leave days</span>
          </div>
        </div>
      )}

      {scorecards.length > 0 && (
        <div className="space-y-2" data-testid="fairness-scorecards">
          {scorecards.map((s) => <Scorecard key={s.signature} s={s} />)}
        </div>
      )}

      {limitations.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-[#475569]"
             data-testid="fairness-limitations">
          <div className="font-medium text-[#0B1A36] mb-1">What this check does not yet do</div>
          <ul className="list-disc list-inside space-y-0.5">
            {limitations.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, tone, hint }) {
  const palette = {
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
    amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700" },
  }[tone] || { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700" };
  return (
    <div className={`rounded-lg border p-4 ${palette.bg} ${palette.border}`}
         data-testid={`fairness-kpi-${tone}`}>
      <div className={`text-[11px] uppercase tracking-widest ${palette.text}`}>{label}</div>
      <div className={`text-3xl font-display mt-1 ${palette.text}`}>{value}</div>
      <div className="text-[11px] text-[#64748B] mt-1">{hint}</div>
    </div>
  );
}

function Scorecard({ s }) {
  const [open, setOpen] = useState(false);
  const meta = BAND_META[s.headline_band] || BAND_META.balanced;
  return (
    <div className="rounded-lg border border-slate-200 bg-white"
         data-testid={`fairness-row-${s.employee_id}`}>
      <button type="button"
              className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50"
              onClick={() => setOpen((o) => !o)}
              data-testid={`fairness-toggle-${s.employee_id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-slate-100 text-[#475569] flex items-center justify-center text-xs font-medium">
            {(s.name || "?").split(/\s+/).map(p => p[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-[#0B1A36] truncate">{s.name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.bg} ${meta.border} ${meta.text}`}>
                {meta.label}
              </span>
              <span className="text-[11px] text-[#64748B]">Click to see the per-dimension breakdown</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <DimensionStrip dims={s.dimensions} />
          {open ? <ChevronUp className="h-4 w-4 text-[#64748B]" /> : <ChevronDown className="h-4 w-4 text-[#64748B]" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2">
          {s.dimensions.map((d) => {
            const dm = BAND_META[d.band] || BAND_META.balanced;
            return (
              <div key={d.key} className={`rounded border ${dm.border} ${dm.bg} px-3 py-2`}
                   data-testid={`fairness-dim-${s.employee_id}-${d.key}`}>
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[#0B1A36]">{d.label}</span>
                  <span className={`text-[10px] uppercase tracking-widest ${dm.text}`}>{dm.label}</span>
                </div>
                {/* Explanation must be MORE prominent than the band label, Tweak 4 */}
                <p className="text-sm text-[#0B1A36] mt-1">{d.explain}</p>
              </div>
            );
          })}
          {s.deep_links?.length > 0 && (
            <div className="flex flex-wrap gap-3 pt-1">
              {s.deep_links.map((dl) => (
                <a key={dl.href} href={dl.href}
                   className="text-xs text-[#1D4ED8] hover:underline inline-flex items-center gap-1"
                   data-testid={`fairness-link-${s.employee_id}-${dl.label.split(" ")[0].toLowerCase()}`}>
                  <ExternalLink className="h-3 w-3" />{dl.label}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DimensionStrip({ dims }) {
  // 6 narrow bars; colour by band.
  return (
    <div className="flex gap-0.5">
      {dims.map((d) => {
        const m = BAND_META[d.band] || BAND_META.balanced;
        return <span key={d.key}
                     title={`${d.label}: ${m.label}`}
                     className={`block w-2 h-6 rounded-sm ${m.bar}`} />;
      })}
    </div>
  );
}

// Subtle People-page chip (Tweak 3).
export function FairnessChip({ employeeId }) {
  const [scorecard, setScorecard] = useState(null);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/simulations/fairness/by-employee/${employeeId}`);
        if (!cancelled) setScorecard(data?.scorecard);
      } catch { /* no sim run yet */ }
      finally { if (!cancelled) setTried(true); }
    })();
    return () => { cancelled = true; };
  }, [employeeId]);
  if (!tried) return null;
  if (!scorecard) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200 text-[#64748B]">
        Fairness: not checked yet
      </span>
    );
  }
  // Subtle label per Tweak 3
  let label = "balanced";
  if (scorecard.watch_outs?.length) {
    const top = scorecard.watch_outs[0];
    const map = {
      weekend_share: "weekend-heavy",
      late_share: "late-shift-heavy",
      holiday_share: "bank-holiday-heavy",
      undesired_share: "less-popular work-heavy",
      total_hours: scorecard.headline_band === "materially_above" ? "higher recent hours" : "lower recent hours",
      leave_share: scorecard.headline_band === "materially_above" ? "more leave this period" : "less leave this period",
    };
    label = map[top] || "needs review";
  }
  const meta = BAND_META[scorecard.headline_band] || BAND_META.balanced;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.bg} ${meta.border} ${meta.text}`}
          data-testid={`fairness-chip-${employeeId}`}
          title="Fairness signal is advisory only">
      <Scale className="h-3 w-3" />
      Fairness: {label}
    </span>
  );
}
